import '@material/web/icon/icon.js'

import { css, html, LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { ZERO_DATA, ZERO_PAGES } from '../configure/zero-config.js'
import { GristData, PaginationConfig } from '../types.js'
import { isMobileDevice, stringifyBigNumber } from '@operato/utils'

@customElement('ox-grid-footer')
export class DataGridFooter extends LitElement {
  static styles = [
    css`
      :host {
        display: flex;
        flex-direction: row;
        gap: var(--spacing-small);
        overflow: hidden;

        padding: var(--grid-footer-padding);
        background-color: var(--grid-footer-background-color);
        font-size: var(--grid-footer-font-size);
        align-items: center;

        user-select: none;
      }

      :host * {
        white-space: nowrap;
        overflow: hidden;

        text-overflow: ellipsis;
        text-align: center;
        color: var(--grid-footer-color);
      }

      .filler {
        flex: 1;
      }

      md-icon {
        font-size: 1.5em;
        vertical-align: middle;

        font-variation-settings: 'FILL' 1;
      }

      .limit {
        display: flex;
        flex-direction: row;
        gap: var(--spacing-small);
        align-items: center;
      }

      .limit a {
        color: var(--grid-footer-limit-color);
      }

      .limit a[selected] {
        color: var(--grid-footer-color);
        font-weight: bold;
        text-decoration: underline;
      }

      a[inactive] * {
        color: var(--grid-footer-inactive-color);
      }

      .pages {
        display: flex;
        flex-direction: row;
        gap: 6px;
        align-items: center;
      }

      .pages a {
        color: var(--grid-footer-limit-color);
        cursor: pointer;
      }

      .pages a[selected] {
        color: var(--grid-footer-color);
        font-weight: bold;
        text-decoration: underline;
        cursor: default;
      }

      .page-input-container {
        margin-left: 10px;
      }

      .page-input {
        width: 30px;
        text-align: center;
        color: var(--grid-footer-limit-color);
        background: transparent;
        border: 1px solid var(--grid-footer-color);
        border-radius: 4px;
        padding: 0.125rem 0.25rem;
      }

      @media screen and (max-width: 460px) {
        .mhidden {
          display: none;
        }
      }
    `
  ]

  @property() data: GristData = ZERO_DATA
  @property() pagination: PaginationConfig = {}

  _gotoPage(page: number) {
    var { pages = ZERO_PAGES } = this.pagination || {}
    var { limit = pages[0], total = 0 } = this.data

    if (page > Math.ceil(total / limit) || page <= 0) {
      return
    }
    this.dispatchEvent(new CustomEvent('page-change', { bubbles: true, composed: true, detail: page }))
  }

  _changeLimit(limit: number) {
    this.dispatchEvent(new CustomEvent('limit-change', { bubbles: true, composed: true, detail: limit }))
  }

  _commitPageInput(e: Event) {
    var target = e.target as HTMLInputElement
    if (!target) return

    var value = parseInt(target.value as string, 10)
    if (isNaN(value)) return

    var { pages = ZERO_PAGES } = this.pagination || {}
    var { limit = pages[0], total = 0 } = this.data
    var totalPage = Math.max(1, Math.ceil(total / limit))

    var targetPage = Math.min(Math.max(1, value), totalPage)
    this._gotoPage(targetPage)
  }

  _onPageInputKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      this._commitPageInput(e as unknown as Event)
    }
  }

  render() {
    var { pages = ZERO_PAGES } = this.pagination || {}
    var { records = [], page = 1, limit = pages[0], total = 0 } = this.data

    var begin = records.length == 0 ? 0 : limit * (page - 1) + 1
    var end = records.length == 0 ? 0 : begin + records.length - 1
    var totalPage = Math.max(1, Math.ceil(total / limit))

    var numbersToShow = Math.min(isMobileDevice() ? 5 : 10, totalPage)
    var startNum = Math.max(1, page - Math.floor(numbersToShow / 2))
    var endNum = startNum + numbersToShow - 1
    if (endNum > totalPage) {
      endNum = totalPage
      startNum = Math.max(1, endNum - numbersToShow + 1)
    }
    var pageNumbers: number[] = []
    for (var i = startNum; i <= endNum; i++) {
      pageNumbers.push(i)
    }

    return html`
      <a ?inactive=${page <= 1} @click=${() => this._gotoPage(1)}><md-icon>skip_previous</md-icon></a>
      <a ?inactive=${page <= 1} @click=${() => this._gotoPage(page - 1)}><md-icon>navigate_before</md-icon></a>
      <span class="pages">
        ${pageNumbers.map(n => html`<a ?selected=${n == page} @click=${() => this._gotoPage(n)}>${n}</a>`)}
      </span>
      <a ?inactive=${page >= totalPage} @click=${() => this._gotoPage(page + 1)}><md-icon>navigate_next</md-icon></a>
      <a ?inactive=${page >= totalPage} @click=${() => this._gotoPage(totalPage)}><md-icon>skip_next</md-icon></a>

      <span class="page-input-container mhidden">
        <input
          class="page-input"
          type="text"
          min="1"
          max=${String(totalPage)}
          .value=${String(page)}
          aria-label="Current page"
          @keydown=${(e: KeyboardEvent) => this._onPageInputKeydown(e)}
          @change=${(e: Event) => this._commitPageInput(e)}
        />
        &nbsp;/&nbsp;${totalPage}
      </span>

      <span class="filler mhidden"></span>

      <span class="limit mhidden">
        ${pages.map(
          size => html`
            <a ?selected=${limit == size} @click=${() => this._changeLimit(size)}>${stringifyBigNumber(size)}</a>
          `
        )}
      </span>
      <span class="mhidden">&nbsp;/&nbsp;</span>
      <span>total ${total || 0} records.</span>
    `
  }
}
